import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpenIcon, UsersIcon, HeartIcon, GlobeIcon, ShieldCheckIcon, ZapIcon, SmileIcon } from 'lucide-react';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
const About = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const teamMembers = [{
    name: 'Sarah Chen',
    role: 'Co-Founder & CEO',
    bio: 'Former indie author and publishing industry veteran.',
    color: 'bg-indigo-100 text-indigo-700'
  }, {
    name: 'Marcus Webb',
    role: 'Co-Founder & CTO',
    bio: 'Built reader communities before building platforms.',
    color: 'bg-blue-100 text-blue-700'
  }, {
    name: 'Priya Sharma',
    role: 'Head of Community',
    bio: 'Romance reader, advocate for diverse voices.',
    color: 'bg-pink-100 text-pink-700'
  }, {
    name: 'James Whitfield',
    role: 'Head of Author Success',
    bio: 'Published 12 books before joining the team.',
    color: 'bg-amber-100 text-amber-700'
  }];
  return <div className={`min-h-screen ${dk.page}`} data-id="element-4146" data-mp="1pk0dubg5fb8q">
      {/* 1. Hero Section */}
      <div className={`py-20 border-b ${dk.border} ${dk.surface}`} data-id="element-4147" data-mp="23iqbptasley5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4148" data-mp="1yjddkceh0lg9">
          <div className="max-w-3xl" data-id="element-4149" data-mp="1yhtz8rcdkkh4">
            <div className={`inline-flex items-center justify-center p-3 rounded-xl mb-6 ${rt.bgMedium} ${rt.textPrimary}`} data-id="element-4150" data-mp="2dgnva3hpinkf">
              <BookOpenIcon className="h-8 w-8" data-id="element-4151" data-mp="28h1ke0pcpjes" />
            </div>
            <h1 className={`text-5xl md:text-6xl font-bold mb-6 tracking-tight ${dk.textHeading}`} data-id="element-4152" data-mp="2m97oupj561ye">
              Built for the indie book world.
            </h1>
            <p className={`text-xl md:text-2xl leading-relaxed ${dk.textSecondary}`} data-id="element-4153" data-mp="2w6m4wo15koq6">
              LitLaunch connects independent authors, service providers, and
              passionate readers in one place.
            </p>
          </div>
        </div>
      </div>

      {/* 2. Mission Statement */}
      <div className={`py-24 ${dk.page}`} data-id="element-4154" data-mp="1sx1a4j9soufi">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-4155" data-mp="1stvj2yhlvih5">
          <blockquote className={`text-3xl md:text-4xl font-medium italic leading-normal mb-8 ${dk.textPrimary}`} data-id="element-4156" data-mp="1xu0491tgl1dp">
            "We believe every story deserves to find its reader — and every
            reader deserves to find their next obsession."
          </blockquote>
          <p className={`text-lg font-medium ${dk.textMuted}`} data-id="element-4157" data-mp="2cie371nr85oz">
            — The LitLaunch Team
          </p>
        </div>
      </div>

      {/* 3. Three Pillars */}
      <div className={`py-20 ${dk.surfaceMuted}`} data-id="element-4158" data-mp="2ll5f12kjl3h5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4159" data-mp="2lijbrf4opa4q">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-4160" data-mp="m92ya03ayn76">
            <div className={`p-8 rounded-2xl border ${dk.card} ${dk.border}`} data-id="element-4161" data-mp="r6cnpfqoyyv0">
              <div className={`inline-flex items-center justify-center h-12 w-12 rounded-lg mb-6 ${rt.bgMedium} ${rt.textPrimary}`} data-id="element-4162" data-mp="jnd87l6cprgx">
                <BookOpenIcon className="h-6 w-6" data-id="element-4163" data-mp="evbyo70kieul" />
              </div>
              <h3 className={`text-xl font-bold mb-3 ${dk.textHeading}`} data-id="element-4164" data-mp="9ylhfgd11d7l">
                For Authors
              </h3>
              <p className={dk.textSecondary} data-id="element-4165" data-mp="501rl4vbbh9y">
                Publish, market, and sell — keeping more of what you earn. We
                provide the tools to build a sustainable career.
              </p>
            </div>
            <div className={`p-8 rounded-2xl border ${dk.card} ${dk.border}`} data-id="element-4166" data-mp="xlbjghpdl7s7">
              <div className={`inline-flex items-center justify-center h-12 w-12 rounded-lg mb-6 ${dk.isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-600'}`} data-id="element-4167" data-mp="sm8d8txnsvin">
                <UsersIcon className="h-6 w-6" data-id="element-4168" data-mp="3dc646wmnvilh" />
              </div>
              <h3 className={`text-xl font-bold mb-3 ${dk.textHeading}`} data-id="element-4169" data-mp="39bvy7jyvcs6m">
                For Readers
              </h3>
              <p className={dk.textSecondary} data-id="element-4170" data-mp="112o5jdcc70qi">
                Discover books that match exactly what you love. No algorithms
                pushing the same bestsellers — just genuine discovery.
              </p>
            </div>
            <div className={`p-8 rounded-2xl border ${dk.card} ${dk.border}`} data-id="element-4171" data-mp="w4w49spsy29n">
              <div className={`inline-flex items-center justify-center h-12 w-12 rounded-lg mb-6 ${dk.isDark ? 'bg-pink-900/30 text-pink-300' : 'bg-pink-100 text-pink-600'}`} data-id="element-4172" data-mp="w2t9ywhm9mz4">
                <HeartIcon className="h-6 w-6" data-id="element-4173" data-mp="1or79jgocsrcs" />
              </div>
              <h3 className={`text-xl font-bold mb-3 ${dk.textHeading}`} data-id="element-4174" data-mp="1at8xwj1f390z">
                For Community
              </h3>
              <p className={dk.textSecondary} data-id="element-4175" data-mp="1ex8e10isc5tt">
                A space where indie publishing thrives. Connect directly with
                creators and fans in a safe, inclusive environment.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 4. Stats Section */}
      <div className={`py-20 border-y ${dk.border} ${dk.surface}`} data-id="element-4176" data-mp="3s3wvl9za6u7l">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4177" data-mp="3n2h9rfpa4jo5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12 text-center" data-id="element-4178" data-mp="5vf2abb82vin">
            <div data-id="element-4179" data-mp="vspg2zb3had">
              <p className={`text-4xl md:text-5xl font-bold mb-2 ${rt.textPrimary}`} data-id="element-4180" data-mp="v96cixx6mur">
                50k+
              </p>
              <p className={`text-sm font-medium uppercase tracking-wide ${dk.textMuted}`} data-id="element-4181" data-mp="399hxdmksn5kt">
                Active Readers
              </p>
            </div>
            <div data-id="element-4182" data-mp="3j2qbys1c53a4">
              <p className={`text-4xl md:text-5xl font-bold mb-2 ${rt.textPrimary}`} data-id="element-4183" data-mp="3j38iqilmizwa">
                10k+
              </p>
              <p className={`text-sm font-medium uppercase tracking-wide ${dk.textMuted}`} data-id="element-4184" data-mp="2qjtfau21jjmv">
                Indie Authors
              </p>
            </div>
            <div data-id="element-4185" data-mp="3b9432048i14e">
              <p className={`text-4xl md:text-5xl font-bold mb-2 ${rt.textPrimary}`} data-id="element-4186" data-mp="3b9nklfpz2nhw">
                100k+
              </p>
              <p className={`text-sm font-medium uppercase tracking-wide ${dk.textMuted}`} data-id="element-4187" data-mp="3k1yy60zzmggg">
                Books Published
              </p>
            </div>
            <div data-id="element-4188" data-mp="2sbkpqkaqtwc6">
              <p className={`text-4xl md:text-5xl font-bold mb-2 ${rt.textPrimary}`} data-id="element-4189" data-mp="2sc2uy6fdvkw8">
                150+
              </p>
              <p className={`text-sm font-medium uppercase tracking-wide ${dk.textMuted}`} data-id="element-4190" data-mp="2weza1n5psyvf">
                Countries
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 5. Values Section */}
      <div className={`py-24 ${dk.page}`} data-id="element-4191" data-mp="gkp6pvfjqwvz">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4192" data-mp="go4m6zd4u7xx">
          <div className="text-center mb-16" data-id="element-4193" data-mp="ljjvrf43xhoj">
            <h2 className={`text-3xl font-bold mb-4 ${dk.textHeading}`} data-id="element-4194" data-mp="262w2gbnt1mou">
              Our Core Values
            </h2>
            <p className={`text-lg max-w-2xl mx-auto ${dk.textSecondary}`} data-id="element-4195" data-mp="2139h1extp04y">
              The principles that guide every decision we make.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-4196" data-mp="1waq4mlavgb1q">
            <div className="text-center" data-id="element-4197" data-mp="1rdge12bqxtcb">
              <div className={`inline-flex items-center justify-center h-16 w-16 rounded-full mb-6 ${rt.bgMedium} ${rt.textPrimary}`} data-id="element-4198" data-mp="1nc4n8th4z22j">
                <ZapIcon className="h-8 w-8" data-id="element-4199" data-mp="1ic0754k549cu" />
              </div>
              <h3 className={`text-xl font-bold mb-3 ${dk.textHeading}`} data-id="element-4200" data-mp="1df9oqakv51o9">
                Indie First
              </h3>
              <p className={dk.textSecondary} data-id="element-4201" data-mp="18gor8iwzww3i">
                We exist to amplify independent voices, not compete with them.
                Every feature is built to empower the creator.
              </p>
            </div>
            <div className="text-center" data-id="element-4202" data-mp="32rbv4gm7oc66">
              <div className={`inline-flex items-center justify-center h-16 w-16 rounded-full mb-6 ${dk.isDark ? 'bg-emerald-900/20 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`} data-id="element-4203" data-mp="2bnixphchig0c">
                <ShieldCheckIcon className="h-8 w-8" data-id="element-4204" data-mp="22upbt12j8idi" />
              </div>
              <h3 className={`text-xl font-bold mb-3 ${dk.textHeading}`} data-id="element-4205" data-mp="26walyenomik0">
                Radical Transparency
              </h3>
              <p className={dk.textSecondary} data-id="element-4206" data-mp="1svq5vq95j0u7">
                No hidden fees, no algorithmic gatekeeping. What you see is what
                you get. We believe in clear, honest business.
              </p>
            </div>
            <div className="text-center" data-id="element-4207" data-mp="1jyzazh6c5s17">
              <div className={`inline-flex items-center justify-center h-16 w-16 rounded-full mb-6 ${dk.isDark ? 'bg-amber-900/20 text-amber-400' : 'bg-amber-50 text-amber-600'}`} data-id="element-4208" data-mp="1nxgabejily82">
                <UsersIcon className="h-8 w-8" data-id="element-4209" data-mp="3iaxz14vnp14o" />
              </div>
              <h3 className={`text-xl font-bold mb-3 ${dk.textHeading}`} data-id="element-4210" data-mp="16omaezh57w11">
                Community Over Commerce
              </h3>
              <p className={dk.textSecondary} data-id="element-4211" data-mp="12olm8n48s6gs">
                Revenue is a means to an end. The community is the end. We
                prioritize healthy interactions over profit maximization.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 6. Team Section */}
      <div className={`py-24 border-t ${dk.border} ${dk.surfaceMuted}`} data-id="element-4212" data-mp="1qbjzlbx2eyah">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4213" data-mp="1lel8714sd5fz">
          <div className="mb-12" data-id="element-4214" data-mp="1lg4mim6vt6f4">
            <h2 className={`text-3xl font-bold mb-4 ${dk.textHeading}`} data-id="element-4215" data-mp="1zdta41sc2rnv">
              Meet the Team
            </h2>
            <p className={`text-lg max-w-2xl ${dk.textSecondary}`} data-id="element-4216" data-mp="1ufrszmegx2f1">
              We are authors, readers, and builders working to change
              publishing.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-4217" data-mp="1idqznl6rz0">
            {teamMembers.map(member => <div key={member.name} className={`flex items-start p-6 rounded-xl border ${dk.card} ${dk.border}`} data-id="element-4218" data-mp="3cfkh1plrtrbo">
                <div className={`h-14 w-14 rounded-full flex items-center justify-center text-lg font-bold mr-5 shrink-0 ${member.color}`} data-id="element-4219" data-mp="33ct3nk9c4lhe">
                  {member.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div data-id="element-4220" data-mp="3r46t9ig3q2yo">
                  <h3 className={`text-lg font-bold ${dk.textHeading}`} data-id="element-4221" data-mp="3r3oyyrd01yte">
                    {member.name}
                  </h3>
                  <p className={`text-sm font-medium mb-2 ${rt.textPrimary}`} data-id="element-4222" data-mp="3hdc86held9et">
                    {member.role}
                  </p>
                  <p className={`text-sm ${dk.textSecondary}`} data-id="element-4223" data-mp="io5as6r6r6l7">{member.bio}</p>
                </div>
              </div>)}
          </div>
        </div>
      </div>

      {/* 7. CTA Section */}
      <div className={`py-24 ${dk.page}`} data-id="element-4224" data-mp="l8jr8580g0cf">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-4225" data-mp="l9uywg4kts8t">
          <h2 className={`text-3xl md:text-4xl font-bold mb-6 ${dk.textHeading}`} data-id="element-4226" data-mp="q6v4yr1feuzx">
            Ready to join the movement?
          </h2>
          <p className={`text-xl mb-10 ${dk.textSecondary}`} data-id="element-4227" data-mp="z7jhfhb68nap">
            Start your journey with LitLaunch today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-4228" data-mp="145ur9s1guxsx">
            <Link to="/create-reader-account" className={`w-full sm:w-auto px-8 py-4 rounded-xl font-bold text-lg transition-transform hover:-translate-y-0.5 ${rt.primaryButton}`} data-id="element-4229" data-mp="1946142rrh8b5">
              Create Reader Account
            </Link>
            <Link to="/create-vendor-account" className={`w-full sm:w-auto px-8 py-4 rounded-xl font-bold text-lg border transition-colors ${dk.textHeading} ${dk.border} hover:${dk.surfaceMuted}`} data-id="element-4230" data-mp="1mxn9qsiwt0me">
              Create Vendor Account
            </Link>
          </div>
        </div>
      </div>
    </div>;
};
export default About;